import { Anchor, Box, Divider, Group, Stack, Text } from "@mantine/core"
import { CubeIcon, EnvelopeSimpleIcon, MapPinIcon, PhoneIcon, TruckIcon, UserIcon } from "@phosphor-icons/react"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"
import { DisabledReason, HelpIcon, i18n, localizeNamePair } from "@dehub/theme"
import type { IDeliveryRoute } from "../../apis/delivery-route"
import { formatReceiverAddress } from "../../utils/format-branch-address"
import { renderBusinessName } from "../../utils/render-business-name"
import { DotPill } from "../delivery/atoms"
import type { DeliveryActionState } from "../delivery/delivery-actions"
import { ReasonTooltip } from "../ui"
import { PriorityPill } from "./dn-detail-header"
import { DnDetail, DnPriority, formatDateTime, formatMoney } from "./dn-detail-utils"
import classes from "./dn-detail.module.css"

function SideCard({ title, action, children, helpKey }: Readonly<{ title: string; action?: ReactNode; children: ReactNode; helpKey?: string }>) {
  return (
    <Box className={classes.sideCard}>
      <Group justify="space-between" align="center" mb="sm">
        <Text className={classes.sideTitle}>{title}{helpKey && <HelpIcon helpKey={helpKey} variant="section" />}</Text>
        {action}
      </Group>
      {children}
    </Box>
  )
}

function SideRow({ icon, children }: Readonly<{ icon: ReactNode; children: ReactNode }>) {
  return (
    <Box className={classes.sideRow}>
      {icon}
      <Box miw={0}>{children}</Box>
    </Box>
  )
}

function KeyValue({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Text className={classes.label}>{label}</Text>
      {children}
    </Group>
  )
}

/** Underlined text action in a card header; unavailable = aria-disabled with the reason (never hidden). */
function CardLink({
  label,
  state,
  onClick,
  helpKey,
  reasonHelpKey,
}: Readonly<{ label: string; state: Pick<DeliveryActionState, "allowed" | "reason">; onClick: () => void; helpKey?: string; reasonHelpKey?: string }>) {
  const control = (
    <Anchor component="button" type="button" className={classes.link} onClick={onClick}>
      {label}
    </Anchor>
  )
  if (state.allowed) {
    // @dehub/help: inline «?» next to the link (R2); hidden by the theme when hints are off or there is no content.
    return helpKey ? (
      <Group gap={4} wrap="nowrap">
        {control}
        <HelpIcon helpKey={helpKey} variant="inline" position="bottom-end" />
      </Group>
    ) : (
      control
    )
  }
  // Unavailable: the reason popover (R4) replaces the plain tooltip when content exists.
  return reasonHelpKey ? (
    <DisabledReason helpKey={reasonHelpKey} reason={/эрх|permission/i.test(state.reason ?? "") ? "PERM" : "NOT_IN_STATUS"} when position="bottom-end">
      {control}
    </DisabledReason>
  ) : (
    <ReasonTooltip reason={state.reason}>{control}</ReasonTooltip>
  )
}

/** Sidebar «Хүлээн авагч»: the buyer (O84), its contact, branch and address (O85); «Профайл» → supplier-side customer page (O90). */
export function DnReceiverCard({ dn }: Readonly<{ dn: DnDetail }>) {
  const { t } = useTranslation("delivery")
  const buyer = dn.receiverBusiness ?? null
  const address = formatReceiverAddress(dn.receiverBranch, dn.receiverWarehouse)
  const buyerId = buyer?.id ?? null
  const navigate = useNavigate()
  return (
    <SideCard
      title={t("dnDetail.customer")}
      action={
        <CardLink
          label={t("dnDetail.profile")}
          state={buyerId ? { allowed: true, reason: null } : { allowed: false, reason: t("dnDetail.noProfile") }}
          onClick={() => buyerId && navigate(`/supplier-customer/detail/${buyerId}`)}
        />
      }
    >
      <Stack gap={8}>
        <Text className={classes.value}>{renderBusinessName(buyer, "-")}</Text>
        <Text className={classes.label}>
          {t("dnDetail.register")}: {buyer?.regNumber || "-"}
        </Text>
        {buyer?.partnerName && (
          <SideRow icon={<UserIcon size={16} />}>{localizeNamePair(buyer.partnerName, buyer.partnerNameEng, i18n.language)}</SideRow>
        )}
        <SideRow icon={<PhoneIcon size={16} />}>
          {dn.receiverPhone ? (
            <Anchor href={`tel:${dn.receiverPhone}`} className={classes.mono} c="var(--nw-text-2)" translate="no">
              {dn.receiverPhone}
            </Anchor>
          ) : (
            "-"
          )}
        </SideRow>
        {buyer?.partnerEmail && (
          <SideRow icon={<EnvelopeSimpleIcon size={16} />}>
            <Anchor href={`mailto:${buyer.partnerEmail}`} c="var(--nw-text-2)" fz="var(--nw-fz-body)">
              {buyer.partnerEmail}
            </Anchor>
          </SideRow>
        )}
        {dn.receiverBranch?.name && <SideRow icon={<CubeIcon size={16} />}>{dn.receiverBranch.name}</SideRow>}
        <SideRow icon={<MapPinIcon size={16} />}>{address ?? t("dnDetail.addressNotSet")}</SideRow>
      </Stack>
    </SideCard>
  )
}

type DriverProps = Readonly<{
  dn: DnDetail
  staffName: string | null
  isLive: boolean
  route: IDeliveryRoute | null
  reassignState: DeliveryActionState
  onReassign: () => void
}>

/** Sidebar «Жолооч, тээврийн хэрэгсэл»: the vehicle comes from the route holding the DN (O96). */
export function DnDriverCard({ dn, staffName, isLive, route, reassignState, onReassign }: DriverProps) {
  const { t } = useTranslation("delivery")
  const vehicle = route?.vehicle ?? null
  const vehicleModel = vehicle ? [vehicle.make, vehicle.model].filter(Boolean).join(" ") : ""
  return (
    <SideCard
      title={t("dnDetail.driverVehicle")}
      helpKey="order.supplier-delivery-detail.section.driver"
      action={
        <CardLink
          label={staffName ? t("dnDetail.change") : t("dnDetail.assignAction")}
          state={reassignState}
          onClick={onReassign}
          helpKey="order.supplier-delivery-detail.action.reassign"
          reasonHelpKey="order.supplier-delivery-detail.reason.reassign"
        />
      }
    >
      <Stack gap="sm">
        {staffName ? (
          <Stack gap={4}>
            <Text className={classes.value}>{staffName}</Text>
            {dn.staff?.phone && (
              <SideRow icon={<PhoneIcon size={16} />}>
                <Anchor href={`tel:${dn.staff.phone}`} className={classes.mono} c="var(--nw-text-2)" translate="no">
                  {dn.staff.phone}
                </Anchor>
              </SideRow>
            )}
            {isLive && <DotPill tone="green">{t("dnDetail.activeOnRoute")}</DotPill>}
          </Stack>
        ) : (
          <Stack gap={2}>
            <Text className={classes.value}>{t("dnDetail.driverNotAssigned")}</Text>
            <Text className={classes.label}>{t("dnDetail.driverAssignHint")}</Text>
          </Stack>
        )}

        <Divider color="var(--nw-table-row-border)" />

        <Group gap="md" wrap="nowrap" align="flex-start">
          <Box className={classes.vehicleIcon}>
            <TruckIcon size={20} />
          </Box>
          <Stack gap={2} miw={0}>
            <Text className={classes.label}>{t("dnDetail.vehicle")}</Text>
            {vehicle ? (
              <>
                <Text className={classes.mono} translate="no">
                  {vehicle.plateNumber}
                </Text>
                {vehicleModel && <Text className={classes.label}>{vehicleModel}</Text>}
              </>
            ) : (
              <>
                <Text className={classes.value}>{t("dnDetail.notAssignedVehicle")}</Text>
                <Text className={classes.label}>{t("dnDetail.vehicleAssignHint")}</Text>
              </>
            )}
            {route && (
              <Anchor component={Link} to={`/delivery-routes/${route.id}`} className={classes.link}>
                {t("dnDetail.vehicleFromRoute", { route: route.routeNumber })}
              </Anchor>
            )}
          </Stack>
        </Group>
      </Stack>
    </SideCard>
  )
}

/** Sidebar «Хугацаа, эрэмбэ». */
export function DnPriorityCard({ dn, priority }: Readonly<{ dn: DnDetail; priority: DnPriority }>) {
  const { t } = useTranslation("delivery")
  return (
    <SideCard title={t("dnDetail.priorityLabel")} helpKey="order.supplier-delivery-detail.section.priority">
      <Stack gap={8}>
        <KeyValue label={t("dnDetail.priority")}>
          <PriorityPill priority={priority} />
        </KeyValue>
        {dn.deliveryDate && (
          <KeyValue label={t("dnDetail.deliverBy")}>
            <Text className={classes.mono}>{formatDateTime(dn.deliveryDate)}</Text>
          </KeyValue>
        )}
        {dn.loadingDate && (
          <KeyValue label={t("dnDetail.loadBy")}>
            <Text className={classes.mono}>{formatDateTime(dn.loadingDate)}</Text>
          </KeyValue>
        )}
        {dn.sla?.slaBreached && <DotPill tone="red">{t("dnDetail.slaBreached")}</DotPill>}
      </Stack>
    </SideCard>
  )
}

/** Sidebar «Холбогдох бүртгэл»: sales order, payment, handover list. */
export function DnLinkedCard({ dn }: Readonly<{ dn: DnDetail }>) {
  const { t } = useTranslation("delivery")
  const paid = dn.paidAmount ?? 0
  const due = dn.amountToPay ?? 0
  return (
    <SideCard title={t("dnDetail.linkedRecords")}>
      <Stack gap={8}>
        <KeyValue label={t("dnDetail.sales")}>
          {dn.orderId ? (
            <Anchor component={Link} to={`/supplier-sales/detail/${dn.orderId}`} className={`${classes.mono} ${classes.link}`} translate="no">
              {dn.salesCode ?? t("dnDetail.viewOrder")}
            </Anchor>
          ) : (
            <Text className={classes.value}>-</Text>
          )}
        </KeyValue>
        <KeyValue label={t("dnDetail.payment")}>
          <Text className={classes.mono} fw={700}>
            {paid > 0
              ? t("dnDetail.paidAmount", { amount: formatMoney(paid) })
              : due > 0
                ? t("dnDetail.receivableAmount", { amount: formatMoney(due) })
                : "-"}
          </Text>
        </KeyValue>
        <KeyValue label={t("dnDetail.handover")}>
          <Anchor component={Link} to="/supplier-delivery?tab=HANDOVER" className={classes.link}>
            {t("dnDetail.handoverLink")}
          </Anchor>
        </KeyValue>
      </Stack>
    </SideCard>
  )
}
